import { useEffect } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";

let lastTrackedPageViewSignature: string | null = null;

const toAbsoluteUrl = (targetUrl: string) => {
    try {
        return new URL(targetUrl, window.location.origin).toString();
    } catch {
        return targetUrl;
    }
};

const isPdfUrl = (targetUrl: string) => {
    try {
        return new URL(targetUrl, window.location.origin).pathname.toLowerCase().endsWith(".pdf");
    } catch {
        return targetUrl.toLowerCase().includes(".pdf");
    }
};

const isExternalHttpUrl = (targetUrl: string) => {
    try {
        const parsedUrl = new URL(targetUrl, window.location.origin);
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
            return false;
        }
        return parsedUrl.origin !== window.location.origin;
    } catch {
        return false;
    }
};

type GaPayloadValue = string | number | boolean;

export const trackGaEvent = (
    eventName: string,
    payload: Record<string, GaPayloadValue>,
    onTracked?: () => void,
) => {
    let hasRunCallback = false;
    const runCallbackOnce = () => {
        if (hasRunCallback || !onTracked) {
            return;
        }
        hasRunCallback = true;
        onTracked();
    };

    ReactGA.gtag("event", eventName, {
        ...payload,
        transport_type: "beacon",
        event_callback: runCallbackOnce,
    });

    // Ensure the user action continues even if callback is blocked.
    if (onTracked) {
        window.setTimeout(runCallbackOnce, 250);
    }
};

export const trackPdfInteraction = (targetUrl: string, source = "unknown", onTracked?: () => void) => {
    trackGaEvent("pdf_interaction", {
        target_url: toAbsoluteUrl(targetUrl),
        source,
    }, onTracked);
};

export const trackExternalLinkClick = (targetUrl: string, source = "unknown", onTracked?: () => void) => {
    trackGaEvent("external_link_click", {
        target_url: toAbsoluteUrl(targetUrl),
        source,
    }, onTracked);
};

export const trackUrlInteraction = (targetUrl: string, source = "unknown") => {
    const absoluteTargetUrl = toAbsoluteUrl(targetUrl);
    if (isPdfUrl(absoluteTargetUrl)) {
        trackPdfInteraction(absoluteTargetUrl, source);
    }
    if (isExternalHttpUrl(absoluteTargetUrl)) {
        trackExternalLinkClick(absoluteTargetUrl, source);
    }
};

export const useGA = (title: string) => {
    const location = useLocation();

    useEffect(() => {
        const page = `${location.pathname}${location.search}${location.hash}`;
        const signature = `${location.key}|${page}|${title}`;

        // Prevent duplicate sends caused by StrictMode remounts in development.
        if (lastTrackedPageViewSignature === signature) {
            return;
        }

        lastTrackedPageViewSignature = signature;
        ReactGA.send({ hitType: "pageview", page, title });
    }, [location.hash, location.key, location.pathname, location.search, title]);
};

export const useTrackLinkInteractions = () => {
    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            const clickTarget = event.target as HTMLElement | null;
            const anchor = clickTarget?.closest("a");
            if (!anchor) {
                return;
            }

            const href = anchor.getAttribute("href");
            if (!href) {
                return;
            }
            if (anchor.getAttribute("data-ga-ignore-auto") === "true") {
                return;
            }

            const absoluteTargetUrl = toAbsoluteUrl(href);
            if (isPdfUrl(absoluteTargetUrl)) {
                trackPdfInteraction(absoluteTargetUrl, "anchor_click");
            }

            if (isExternalHttpUrl(absoluteTargetUrl)) {
                trackExternalLinkClick(absoluteTargetUrl, "anchor_click");
            }
        };

        document.addEventListener("click", handleDocumentClick, true);
        return () => {
            document.removeEventListener("click", handleDocumentClick, true);
        };
    }, []);
};
